import {
  Button,
  ButtonType,
  DateFormat,
  Modal,
  ModalBodyWithSections,
  ModalFooter,
  ModalHeader,
  MultiSelectInput,
  DatePickerInput,
  colors,
  icons,
  formatDate,
  noop,
  parseDate,
} from '@chillibean/component-library';
import { Accordion } from '@chillibean/component-library/legacy';
import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { useFilterParams } from '../../hooks';
import DatePickerInputWrapper from '../../utils/styledComponents/datePickerComponents';

export const ACCORDION_TYPES = {
  CRITERIA: 'criteria',
  DATE: 'date',
};

export function FilterModal(props) {
  const {
    defaultAccordionSection = ACCORDION_TYPES.CRITERIA,
    onClose = noop,
    updateValues = noop,
    values = {
      actions: [],
      date_from: '',
      date_to: '',
      locations: [],
      performers: [],
    },
    visible = false,
  } = props;

  const { formatFiltersForParams } = useFilterParams();
  const [accordionOpened, setAccordionOpened] = useState('');

  const filterGroupLength = useMemo(
    () => values.actions.length + values.locations.length + values.performers.length,
    [values.actions, values.locations, values.performers],
  );

  const filterDates = useMemo(() => {
    const dates = [];
    const parsedDateFrom = parseDate(values.date_from, DateFormat.ISO_DATE);
    const parsedDateTo = parseDate(values.date_to, DateFormat.ISO_DATE);
    if (values.date_from) dates.push(formatDate(parsedDateFrom, 'dd MMM yyyy'));
    if (values.date_to) dates.push(formatDate(parsedDateTo, 'dd MMM yyyy'));
    if (dates.length === 0) return '';

    return dates.length > 1 ? dates.join(' - ') : dates[0];
  }, [values.date_from, values.date_to]);

  const {
    filteringActions: { data: actions },
    filteringLocations: { data: locations },
    filteringPerformers: { data: performers },
  } = useSelector((state) => state.filteringReducer);

  const handleClose = () => {
    setAccordionOpened('');
    onClose();
  };

  const handleApplyFilters = () => {
    formatFiltersForParams({ values });
    handleClose();
  };

  const handleFilterGroupClear = (e) => {
    e.stopPropagation();

    updateValues((prev) => ({
      ...prev,
      actions: [],
      locations: [],
      performers: [],
    }));
  };

  const handleDatesClear = (e) => {
    e.stopPropagation();

    updateValues((prev) => ({
      ...prev,
      date_from: '',
      date_to: '',
    }));
  };

  const handleToggleAccordion = (id, isOpen) => {
    setAccordionOpened(isOpen ? id : '');
  };

  return (
    <Modal onClose={handleClose} visible={visible}>
      <ModalHeader closeable onClose={handleClose} title="Filter Activity Log" />
      <ModalBodyWithSections overflow="visible">
        <AccordionWrapper>
          <Accordion
            closeAccordion={accordionOpened === ACCORDION_TYPES.DATE}
            content={
              <MultiFilterContainer>
                <MultiSelectInput
                  disabled={false}
                  error=""
                  id="actions-list"
                  label="Actions"
                  onChange={(e) => updateValues((prev) => ({ ...prev, actions: e.target.value }))}
                  options={actions?.map((a) => a.multiSelectParams())}
                  placeholder="Actions"
                  required={false}
                  value={values.actions}
                />
                <MultiSelectInput
                  disabled={false}
                  error=""
                  id="locations-list"
                  label="Locations"
                  onChange={(e) => updateValues((prev) => ({ ...prev, locations: e.target.value }))}
                  options={locations?.map((l) => l.multiSelectParams())}
                  placeholder="Locations"
                  required={false}
                  value={values.locations}
                />
                <MultiSelectInput
                  disabled={false}
                  error=""
                  id="performers-list"
                  label="Performers"
                  onChange={(e) => updateValues((prev) => ({ ...prev, performers: e.target.value }))}
                  options={performers?.map((p) => p.multiSelectParams())}
                  placeholder="Performers"
                  required={false}
                  value={values.performers}
                />
              </MultiFilterContainer>
            }
            icon={<Icon color={colors.black.primary} height={24} icon={icons.baselineChevronDown} width={24} />}
            id={ACCORDION_TYPES.CRITERIA}
            item={
              <AccordionItemWrapper>
                <AccordionHeader>Filter by Criteria</AccordionHeader>
                {filterGroupLength > 0 ? (
                  <AccordionSubHeader>
                    <SubHeader>{filterGroupLength} filters applied</SubHeader>
                    <Icon
                      color={colors.black.primary}
                      height={16}
                      icon={icons.closeCircleOutline}
                      onClick={handleFilterGroupClear}
                      width={16}
                    />
                  </AccordionSubHeader>
                ) : (
                  <AccordionSubHeader>All activity</AccordionSubHeader>
                )}
              </AccordionItemWrapper>
            }
            onToggle={handleToggleAccordion}
            openByDefault={defaultAccordionSection === ACCORDION_TYPES.CRITERIA}
            overflow="visible"
          />
        </AccordionWrapper>
        <AccordionWrapper>
          <Accordion
            closeAccordion={accordionOpened === ACCORDION_TYPES.CRITERIA}
            content={
              <DatePickerSection>
                <DatePickerInputWrapper>
                  <DatePickerInput
                    disabled={false}
                    error=""
                    id="minimum-date"
                    label="From"
                    maxDate={values.date_to ? new Date(values.date_to) : new Date()}
                    onChange={(e) => updateValues((prev) => ({ ...prev, date_from: e.target.value }))}
                    options={performers?.map((p) => p.multiSelectParams())}
                    placeholder="From"
                    required={false}
                    value={values.date_from}
                  />
                </DatePickerInputWrapper>
                <DatePickerInputWrapper>
                  <DatePickerInput
                    disabled={false}
                    error=""
                    id="maximum-date"
                    label="To"
                    maxDate={new Date()}
                    minDate={values.date_from ? new Date(values.date_from) : undefined}
                    onChange={(e) => updateValues((prev) => ({ ...prev, date_to: e.target.value }))}
                    placeholder="To"
                    required={false}
                    value={values.date_to}
                  />
                </DatePickerInputWrapper>
              </DatePickerSection>
            }
            icon={<Icon color={colors.black.primary} height={24} icon={icons.baselineChevronDown} width={24} />}
            id={ACCORDION_TYPES.DATE}
            item={
              <AccordionItemWrapper>
                <AccordionHeader>Filter by Event Date</AccordionHeader>
                {filterDates.length > 0 ? (
                  <AccordionSubHeader>
                    <SubHeader>{filterDates}</SubHeader>
                    <Icon
                      color={colors.black.primary}
                      height={16}
                      icon={icons.closeCircleOutline}
                      onClick={handleDatesClear}
                      width={16}
                    />
                  </AccordionSubHeader>
                ) : (
                  <AccordionSubHeader>All time</AccordionSubHeader>
                )}
              </AccordionItemWrapper>
            }
            onToggle={handleToggleAccordion}
            openByDefault={defaultAccordionSection === ACCORDION_TYPES.DATE}
            overflow="visible"
          />
        </AccordionWrapper>
      </ModalBodyWithSections>
      <ModalFooter>
        <Button onClick={handleClose} text="CLOSE" type={ButtonType.TERTIARY} />
        <Button onClick={handleApplyFilters} text="APPLY" type={ButtonType.PRIMARY} />
      </ModalFooter>
    </Modal>
  );
}

const AccordionWrapper = styled.div`
  background-color: blue;
  padding: 8px 16px;
  background-color: ${colors.white.primary};
  border-radius: 4px;
`;

const AccordionItemWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AccordionHeader = styled.div`
  color: ${colors.black.primary};
  font-size: 13px;
  font-weight: 700;
`;

const AccordionSubHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.black.primary};
`;

const SubHeader = styled.span`
  color: ${colors.black.primary};
  font-size: 13px;
  font-weight: 400;
`;

const MultiFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  padding: 16px;
`;

const DatePickerSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
`;

FilterModal.propTypes = {
  defaultAccordionSection: PropTypes.oneOf(Object.values(ACCORDION_TYPES)),
  onClose: PropTypes.func,
  updateValues: PropTypes.func,
  values: PropTypes.shape({
    actions: PropTypes.arrayOf(PropTypes.string),
    date_from: PropTypes.string,
    date_to: PropTypes.string,
    locations: PropTypes.arrayOf(PropTypes.string),
    performers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  }),
  visible: PropTypes.bool,
};

export default FilterModal;
